import { useMemo } from 'react';

import { useForecastingData } from '../../../hooks/use-forecasting-data';
import { generateChartDataFromApiResponse } from '../utils/generate-chart-data';

/**
 * @typedef {import('../utils/generate-chart-data').ChartDataPoint} ChartDataPoint
 */

/**
 * @typedef {object} GridChartDataReturn
 * @property {Array<ChartDataPoint>} chartData - Array of chart data points
 * @property {boolean} isLoading - Whether the chart data is currently loading
 */

/**
 * Hook to generate chart data for specific products in the forecasting grid
 * @param {string} productId - The ID of the product to generate chart data for
 * @returns {GridChartDataReturn} Object containing chartData and isLoading state
 */
export const useGridChartData = (productId) => {
  const { data, quantityValues, orderVelocityData, forecastPeriod, salesTrend, leadTimeDays, bufferStockDays } = useForecastingData();

  const currentProductQuantity = quantityValues[productId];

  const chartData = useMemo(() => {
    const product = data.find((item) => item.id === productId);
    if (!product) {
      return null;
    }

    const missingChartData = !product.sku || !orderVelocityData?.data || !product.hasChartData;
    if (missingChartData) {
      return null;
    }

    const skuData = orderVelocityData.data.find((item) => item.skuId === product.sku);
    if (skuData) {
      const currentQuantity = currentProductQuantity || product.suggestedReorder;
      return generateChartDataFromApiResponse(
        skuData,
        product.currentOnHand,
        currentQuantity,
        forecastPeriod,
        salesTrend,
        leadTimeDays,
        bufferStockDays,
      );
    }

    return null;
  }, [productId, data, currentProductQuantity, orderVelocityData, forecastPeriod, salesTrend, leadTimeDays, bufferStockDays]);

  // Only show loading when processing data and there's historical data available
  // If chartData is null because there's no historical data, don't show loading
  const product = data.find((item) => item.id === productId);
  const hasHistoricalData = product?.hasChartData;

  const isLoading = hasHistoricalData && chartData == null;

  return {
    chartData: chartData || [],
    isLoading,
  };
};

export default useGridChartData;
